import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Mail, Clock, Package, Calendar, Globe, Target } from 'lucide-react';
import SettingsProfile from './SettingsProfile';
import SettingsLanguage from './SettingsLanguage';
import SettingsGmail from './SettingsGmail';
import SettingsOutlook from './SettingsOutlook';
import SettingsCommsSchedule from './SettingsCommsSchedule';
import SettingsPersonalization from './SettingsPersonalization';
import SettingsEvents from './SettingsEvents';
import SettingsProductProfile from './SettingsProductProfile';
import SettingsEmailTracking from './SettingsEmailTracking';
import { useTranslation } from '@/components/utils/translations';
import { useTenantCapabilities } from '@/contexts/UserContext';
import {
  CORE_SETTINGS_TABS,
  PROCURE_ONLY_SETTINGS_TABS,
} from '@/lib/tenantCapabilities';

export default function Settings({ currentTenant }) {
  const { t } = useTranslation();
  const { showCore } = useTenantCapabilities();
  const allowedTabIds = showCore ? CORE_SETTINGS_TABS : PROCURE_ONLY_SETTINGS_TABS;
  const defaultTab = showCore ? 'profile' : 'language';

  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = allowedTabIds.includes(tabFromUrl) ? tabFromUrl : defaultTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (allowedTabIds.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else if (tabFromUrl && !allowedTabIds.includes(tabFromUrl)) {
      setActiveTab(defaultTab);
    }
  }, [tabFromUrl, showCore, defaultTab]);

  return (
    <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
      <div className="bg-white border-b mb-6 -mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-8">
          <h1 className="text-3xl font-bold text-[#0A1A2F]">{t('settings')}</h1>
          <p className="text-lg text-[#5C6B7A] mt-1">{t('configureWorkspacePreferences')}</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-0">

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList
            className={`grid w-full max-w-5xl ${showCore ? 'grid-cols-7' : 'grid-cols-2'}`}
          >
            {showCore && (
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="hidden md:inline">{t('profile')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="language" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span className="hidden md:inline">{t('language')}</span>
            </TabsTrigger>
            {showCore && (
              <>
                <TabsTrigger value="gmail" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="hidden md:inline">{t('emails')}</span>
                </TabsTrigger>
                <TabsTrigger value="schedule" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span className="hidden md:inline">{t('schedule')}</span>
                </TabsTrigger>
                <TabsTrigger value="personalization" className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  <span className="hidden md:inline">{t('personalization')}</span>
                </TabsTrigger>
                <TabsTrigger value="events" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span className="hidden md:inline">{t('events')}</span>
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="product-profile" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span className="hidden md:inline">{t('productFit')}</span>
            </TabsTrigger>
          </TabsList>

          {showCore && (
            <TabsContent value="profile">
              <SettingsProfile />
            </TabsContent>
          )}

          <TabsContent value="language">
            <SettingsLanguage />
          </TabsContent>

          {showCore && (
            <>
              <TabsContent value="gmail">
                <SettingsGmail currentTenant={currentTenant} />
                <div className="mt-6">
                  <SettingsOutlook currentTenant={currentTenant} />
                </div>
                <div className="mt-6">
                  <SettingsEmailTracking />
                </div>
              </TabsContent>

              <TabsContent value="schedule">
                <SettingsCommsSchedule currentTenant={currentTenant} />
              </TabsContent>

              <TabsContent value="personalization">
                <SettingsPersonalization currentTenant={currentTenant} />
              </TabsContent>

              <TabsContent value="events">
                <SettingsEvents currentTenant={currentTenant} />
              </TabsContent>
            </>
          )}

          <TabsContent value="product-profile">
            <SettingsProductProfile currentTenant={currentTenant} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}